/*****************************************************
 * CustomerDashboard.jsx
 * Versie: 3.0.0 — Sidebar redesign
 * Datum: 2026-03-29
 * Auteur: Claude + Ragnar
 ******************************************************
 * NIEUW v3.0.0:
 * - Volledige sidebar navigatie (navy/gold huisstijl)
 * - Dashboard, Aanvragen, Track & Trace, Analytics,
 *   Facturen, Berichten, Help als aparte secties
 * - Track & Trace als eigen knop (was inline)
 * - Analytics als eigen knop (nieuw)
 * - Geofence toasts behouden
 *****************************************************/

import { useLanguage } from '../LanguageContext';
import TaalWisselaar from '../TaalWisselaar';
import CustomerBordero from '../components/CustomerPortal/CustomerBordero';
import VasteZendingenBeheer from '../components/VasteZendingenBeheer';
import PrintDialog from '../components/CustomerPortal/PrintDialog';
import ZendingTijdlijn from '../components/CustomerPortal/ZendingTijdlijn';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import CustomerAanvragenModal from '../components/CustomerPortal/CustomerAanvragenModal';
import AanvraagDetailPanel from '../components/CustomerPortal/AanvraagDetailPanel';
import CustomerNewRequest from '../pages/CustomerNewRequest';
import CustomerSettingsModal from '../components/CustomerPortal/CustomerSettingsModal';
import CustomerMessages from '../pages/CustomerMessages';
import CustomerHelpCenter from '../pages/CustomerDashboard/CustomerHelpCenter';
import CustomerTrackingMap from '../components/CustomerPortal/CustomerTrackingMap';
import CustomerAnalytics from '../pages/CustomerAnalytics';

// ─── CSS variabelen & globale stijlen ────────────────────────────────────────
const G = {
  navy:      '#1a364c',
  navyDeep:  '#0f1f2b',
  navyMid:   '#24496a',
  navyLight: '#2d5a7a',
  gold:      '#d4a848',
  goldLight: '#f0c96a',
  goldDim:   'rgba(212,168,72,0.15)',
  cream:     '#f7f6f2',
  white:     '#ffffff',
  muted:     '#94a3b8',
  border:    'rgba(255,255,255,0.08)',
  borderL:   '#e8edf3',
  green:     '#10b981',
  amber:     '#f59e0b',
  red:       '#ef4444',
  blue:      '#3b82f6',
  txt:       '#1e293b',
  txt2:      '#64748b',
  txt3:      '#94a3b8',
};

// ─── Geofence toast ───────────────────────────────────────────────────────────
const GeofencePortalToast = () => {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail;
      const id = Date.now() + Math.random();
      setToasts(prev => [...prev.slice(-3), { ...d, id }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 8000);
    };
    window.addEventListener('portal-geofence', handler);
    return () => window.removeEventListener('portal-geofence', handler);
  }, []);
  if (!toasts.length) return null;
  return (
    <div style={{ position:'fixed', bottom:24, right:24, zIndex:99999, display:'flex', flexDirection:'column', gap:10, pointerEvents:'none' }}>
      {toasts.map(t => {
        const enter = t.type === 'ENTER';
        const tijd = new Date(t.timestamp).toLocaleTimeString('nl-NL', { hour:'2-digit', minute:'2-digit' });
        return (
          <div key={t.id} style={{ background:'white', borderRadius:10, padding:'12px 16px', border:`2px solid ${enter?'#059669':'#d97706'}`, boxShadow:'0 4px 20px rgba(0,0,0,0.15)', minWidth:280, pointerEvents:'all' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
              <span style={{ fontSize:18 }}>{t.hub?.type === 'centrale_hub' ? '⭐' : '📍'}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:enter?'#059669':'#d97706' }}>
                  {enter ? 'Hub bereikt' : 'Hub verlaten'} — {t.hub?.naam}
                </div>
                <div style={{ fontSize:11, color:G.txt3 }}>{t.hub?.stad}{t.hub?.land ? `, ${t.hub.land}` : ''} · {tijd}</div>
              </div>
            </div>
            {t.aanvraag && (
              <div style={{ fontSize:12, color:G.txt, background:'#f9fafb', borderRadius:6, padding:'5px 8px' }}>
                📦 {t.aanvraag.id} · {t.aanvraag.van} → {t.aanvraag.naar}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Badge helper ─────────────────────────────────────────────────────────────
const Badge = ({ n, color = G.red }) => n > 0 ? (
  <span style={{ marginLeft:'auto', background:color, color:'white', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, lineHeight:1.4 }}>{n}</span>
) : null;

// ─── Nav iconen ───────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16 }) => {
  const s = { width:size, height:size, flexShrink:0 };
  const paths = {
    home:     <path d="M2 6.5L8 2l6 4.5V14a.5.5 0 01-.5.5h-3.5V10h-4v4.5H2.5A.5.5 0 012 14V6.5z"/>,
    list:     <><rect x="2" y="2" width="12" height="12" rx="1.5"/><path d="M5 6h6M5 8.5h4M5 11h6"/></>,
    track:    <><circle cx="8" cy="8" r="6"/><path d="M8 4v4l2.5 2"/><circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none"/></>,
    analytics:<><path d="M2 13l4-5 3 3 3-4 2 2"/><rect x="2" y="12.5" width="12" height="1" rx=".5" fill="currentColor" stroke="none"/></>,
    bordero:<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>,
    print:<><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></>,
    factuur:  <><path d="M10 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V5l-3-3z"/><path d="M10 2v3h3M6 8h4M6 10.5h2.5"/></>,
    msg:      <path d="M14 10.5a1.5 1.5 0 01-1.5 1.5H4l-2 2V3.5A1.5 1.5 0 013.5 2h9A1.5 1.5 0 0114 3.5v7z"/>,
    help:     <><circle cx="8" cy="8" r="6"/><path d="M6.5 6.5a1.5 1.5 0 012.9.5c0 1-1.4 1.5-1.4 2.5"/><circle cx="8" cy="11.5" r=".5" fill="currentColor" stroke="none"/></>,
    logout:   <><path d="M10.5 10.5L14 8l-3.5-2.5M6 8h8M5.5 3H3a1 1 0 00-1 1v8a1 1 0 001 1h2.5"/></>,
    settings: <><circle cx="8" cy="8" r="2.5"/><path d="M8 2v1.5M8 12.5V14M2 8h1.5M12.5 8H14M3.5 3.5l1 1M11.5 11.5l1 1M3.5 12.5l1-1M11.5 4.5l1-1"/></>,
    plus:     <><path d="M8 2v12M2 8h12"/></>,
    refresh:  <path d="M2 8a6 6 0 1010.5-4L10 2M2 8L4 4"/>,
  };
  return (
    <svg style={s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      {paths[name]}
    </svg>
  );
};

// ─── Status mapping ───────────────────────────────────────────────────────────
const KLANT_STATUS = (s, t) => {
  if (!s) return { label: t ? t('onbekend') : 'Onbekend', color: G.txt3, bg: '#f1f5f9' };
  const sl = s.toLowerCase();
  if (sl.includes('geoffreerd') || sl.includes('prijs')) return { label: t ? t('status_prijs_ontvangen') : 'Prijs ontvangen', color: '#1e40af', bg: '#dbeafe' };
  if (sl.includes('opdracht ontvangen')) return { label: t ? t('status_akkoord_gegeven') : 'Akkoord gegeven', color: '#92400e', bg: '#fef3c7' };
  if (sl.includes('opdracht verzonden')) return { label: t ? t('status_opdracht_bevestigd') : 'Opdracht bevestigd', color: '#065f46', bg: '#d1fae5' };
  if (sl.includes('onderweg')) return { label: t ? t('status_onderweg') : 'Onderweg', color: '#92400e', bg: '#fef3c7' };
  if (sl.includes('afgerond') || sl.includes('afgeleverd')) return { label: t ? t('status_afgeleverd') : 'Afgeleverd', color: '#065f46', bg: '#d1fae5' };
  if (sl.includes('geannuleerd') || sl.includes('afgeschreven')) return { label: t ? t('status_geannuleerd') : 'Geannuleerd', color: '#991b1b', bg: '#fee2e2' };
  return { label: t ? t('status_in_behandeling') : 'In behandeling', color: G.txt2, bg: '#f1f5f9' };
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, trend, trendUp, accent, highlight = false }) => (
  <div style={{ background: highlight ? '#fff5f5' : G.white, borderRadius:10, padding:'18px 20px', border:`1px solid ${highlight ? '#fca5a5' : G.borderL}`, position:'relative', overflow:'hidden', transition:'.2s' }}>
    <div style={{ position:'absolute', top:0, left:0, right:0, height: highlight ? 5 : 3, background:accent, borderRadius:'10px 10px 0 0' }} />
    <div style={{ fontSize:11.5, color: highlight ? G.red : G.txt2, fontWeight: highlight ? 600 : 500, letterSpacing:'.02em', marginBottom:10, marginTop:2 }}>{label}</div>
    <div style={{ fontSize:26, fontWeight:700, color: highlight ? G.red : G.txt, letterSpacing:'-.5px' }}>{value}</div>
    {trend && (
      <div style={{ fontSize:12, marginTop:6, color: trendUp === true ? G.green : trendUp === false ? G.red : G.txt3 }}>
        {trend}
      </div>
    )}
  </div>
);

// ─── Activiteit item ──────────────────────────────────────────────────────────
const EventRow = ({ icon, iconBg, title, sub, time }) => (
  <div style={{ display:'flex', gap:12, padding:'10px 0', borderBottom:`1px solid #f8fafc` }}>
    <div style={{ width:28, height:28, borderRadius:'50%', background:iconBg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, flexShrink:0, marginTop:1 }}>{icon}</div>
    <div style={{ flex:1 }}>
      <div style={{ fontSize:13, color:G.txt, fontWeight:500 }}>{title}</div>
      <div style={{ fontSize:12, color:G.txt2, marginTop:2 }}>{sub}</div>
    </div>
    <div style={{ fontSize:11, color:G.txt3, whiteSpace:'nowrap', paddingTop:2 }}>{time}</div>
  </div>
);

// ─── Aanvragen tabel ──────────────────────────────────────────────────────────
const AanvragenTabel = ({ aanvragen, onOpen, compact = false }) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = React.useState(null);
  const slice = compact ? aanvragen.slice(0, 5) : aanvragen;
  return (
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
      <thead>
        <tr>
          {[
            { key:'aanvraag',   label: t('aanvraag') },
            { key:'datum',      label: t('datum') },
            { key:'bestemming', label: t('bestemming') },
            { key:'goederen',   label: t('goederen') },
            { key:'laaddatum',  label: t('laaddatum') },
            { key:'losdatum',   label: t('losdatum') },
            { key:'status',     label: t('status') },
            { key:'actie',      label: t('actie') },
          ].map(({ key, label }) => (
            <th key={key} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:G.txt3, fontWeight:600, letterSpacing:'.06em', textTransform:'uppercase', borderBottom:`1px solid #f1f5f9` }}>{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {slice.map((a, i) => {
          const st = KLANT_STATUS(a.Status, t);
          const datum = a.Datum ? String(a.Datum).slice(0, 10).split('-').reverse().join('-') : '—';
          const los = [a.LosPlaats, a.LosLand].filter(Boolean).join(', ') || '—';
          const goed = a.Goed1_Omschrijving || a.Goed1_Soort || '—';
          const isExp = expanded === (a.AanvraagID || i);
          return (
            <React.Fragment key={a.AanvraagID || i}>
              <tr
                style={{ cursor:'pointer' }}
                onClick={() => { setExpanded(isExp ? null : (a.AanvraagID || i)); onOpen && onOpen(a); }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
              <td style={{ padding:'11px 12px', fontWeight:600, color:G.navy }}>{a.AanvraagID}</td>
              <td style={{ padding:'11px 12px', color:G.txt }}>{datum}</td>
              <td style={{ padding:'11px 12px', color:G.txt }}>{los}</td>
              <td style={{ padding:'11px 12px', color:G.txt2 }}>{goed}</td>
              <td style={{ padding:'11px 12px', color:G.txt }}>{a.LaadDatum ? String(a.LaadDatum).slice(0,10).split('-').reverse().join('-') : '—'}</td>
              <td style={{ padding:'11px 12px', color:G.txt }}>{a.LosDatum ? String(a.LosDatum).slice(0,10).split('-').reverse().join('-') : '—'}</td>
              <td style={{ padding:'11px 12px' }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11.5, fontWeight:500, padding:'3px 9px', borderRadius:20, background:st.bg, color:st.color }}>{st.label}</span>
              </td>
              <td style={{ padding:'11px 12px' }}>
                {(() => {
                  const sl = (a.Status||'').toLowerCase();
                  if (sl.includes('geoffreerd')) return (
                    <span style={{ fontSize:11.5, fontWeight:600, color:'#1e40af', background:'#dbeafe', padding:'3px 9px', borderRadius:20 }}>{t('akkoord_geven')} →</span>
                  );
                  if (sl.includes('opdracht ontvangen')) return (
                    <span style={{ fontSize:11.5, color:G.txt2 }}>{t('wacht_op_bevestiging')}</span>
                  );
                  if (sl.includes('opdracht verzonden')) return (
                    <span style={{ fontSize:11.5, color:G.green, fontWeight:500 }}>✓ {t('status_opdracht_bevestigd')}</span>
                  );
                  if (sl.includes('onderweg')) return (
                    <span style={{ fontSize:11.5, color:G.txt2 }}>{t('status_onderweg')}</span>
                  );
                  if (sl.includes('afgerond') || sl.includes('afgeleverd')) return (
                    <span style={{ fontSize:11.5, color:G.green }}>✓ {t('status_afgeleverd')}</span>
                  );
                  return <span style={{ fontSize:11.5, color:G.txt3 }}>—</span>;
                })()}
              </td>
            </tr>
            {isExp && (
              <tr>
                <td colSpan={8} style={{ padding:0, borderBottom:'1px solid #f1f5f9' }}>
                  <ZendingTijdlijn aanvraag={a} />
                </td>
              </tr>
            )}
          </React.Fragment>
          );
        })}
        {slice.length === 0 && (
          <tr><td colSpan={5} style={{ padding:'24px 12px', textAlign:'center', color:G.txt3, fontSize:13 }}>{t('geen_aanvragen')}</td></tr>
        )}
      </tbody>
    </table>
  );
};

// ─── Track & Trace sectie ─────────────────────────────────────────────────────
const TrackingSection = ({ aanvragen, klantInfo }) => {
  const actief = aanvragen.filter(a => {
    const sl = (a.Status || '').toLowerCase();
    return sl.includes('onderweg') || sl.includes('opdracht verzonden') || sl.includes('opdracht ontvangen');
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
      {/* Kaart — vult volledige ruimte */}
      <div style={{ flex:1, minHeight:500, overflow:'hidden' }}>
        <CustomerTrackingMap aanvragen={aanvragen} />
      </div>



      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
    </div>
  );
};

// ─── Facturen sectie ──────────────────────────────────────────────────────────
const FacturenSection = ({ klantInfo }) => {
  const { t } = useLanguage();
  const [facturen, setFacturen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoek, setZoek] = useState('');
  const [filterStatus, setFilterStatus] = useState('alle');

  useEffect(() => {
    fetch('/api/customer/facturen', { credentials:'include' })
      .then(r => r.json())
      .then(d => setFacturen(d.facturen || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openPdf = async (factuurNr) => {
    try {
      const res = await fetch(`/api/customer/factuur-pdf/${factuurNr}`, { credentials:'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        alert('Factuur PDF niet beschikbaar. Neem contact op met SendWell.');
      }
    } catch(e) {
      alert('Fout bij ophalen factuur.');
    }
  };

  const fmtD = (s) => {
    if (!s) return '—';
    try { return new Date(s instanceof Date ? s : s).toLocaleDateString('nl-NL', { day:'2-digit', month:'2-digit', year:'numeric' }); }
    catch(e) { return '—'; }
  };
  const fmt = (n) => n ? `€ ${Number(n).toLocaleString('nl-NL', { minimumFractionDigits:2, maximumFractionDigits:2 })}` : '—';

  const nu = new Date();
  const verlooptOver = (vervalDatum) => {
    if (!vervalDatum) return null;
    try {
      const vd = new Date(vervalDatum instanceof Date ? vervalDatum : vervalDatum);
      const diff = Math.ceil((vd - nu) / (1000*60*60*24));
      return diff;
    } catch(e) { return null; }
  };

  const gefilterd = facturen.filter(f => {
    const status = f.Status || f.FactuurStatus || '';
    const betaald = status === 'Betaald';
    if (filterStatus === 'open' && betaald) return false;
    if (filterStatus === 'betaald' && !betaald) return false;
    if (zoek) {
      const z = zoek.toLowerCase();
      return (f.FactuurNr||'').toLowerCase().includes(z) || (f.AanvraagID||'').toLowerCase().includes(z) || (f.Goederen||'').toLowerCase().includes(z);
    }
    return true;
  });

  const totaalOpen = facturen.filter(f => (f.Status||f.FactuurStatus) !== 'Betaald').reduce((s,f) => s+(parseFloat(f.TotaalPrijs)||0), 0);
  const totaalBetaald = facturen.filter(f => (f.Status||f.FactuurStatus) === 'Betaald').reduce((s,f) => s+(parseFloat(f.TotaalPrijs)||0), 0);

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ fontSize:22, fontWeight:600, color:G.txt, letterSpacing:'-.4px', margin:0 }}>{t('nav_facturen')}</h2>
        <p style={{ fontSize:13, color:G.txt2, margin:'4px 0 0' }}>{t('facturen_overzicht')}</p>
      </div>

      {/* KPI cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0,1fr))', gap:12, marginBottom:20 }}>
        <KpiCard label={t("openstaand")} value={fmt(totaalOpen)} trend={`${facturen.filter(f=>(f.Status||f.FactuurStatus)!=='Betaald').length} ${t('facturen')}`} trendUp={null} accent={`linear-gradient(90deg,${G.red},#f87171)`} />
        <KpiCard label={t('betaald')} value={fmt(totaalBetaald)} trend={`${facturen.filter(f=>(f.Status||f.FactuurStatus)==='Betaald').length} ${t('facturen')}`} trendUp={true} accent={`linear-gradient(90deg,${G.green},#34d399)`} />
        <KpiCard label={t('totaal')} value={fmt(totaalOpen+totaalBetaald)} trend={`${facturen.length} ${t('facturen')} ${t('totaal').toLowerCase()}`} trendUp={null} accent={`linear-gradient(90deg,${G.gold},#fbbf24)`} />
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'center' }}>
        <input
          type="text" placeholder={t('zoeken')}
          value={zoek} onChange={e => setZoek(e.target.value)}
          style={{ flex:1, padding:'8px 12px', border:`1px solid ${G.border}`, borderRadius:7, fontSize:13, outline:'none' }}
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding:'8px 12px', border:`1px solid ${G.border}`, borderRadius:7, fontSize:13, background:G.white, cursor:'pointer' }}>
          <option value="alle">{t("alle_statussen")}</option>
          <option value="open">{t("openstaand")}</option>
          <option value="betaald">{t('betaald')}</option>
        </select>
      </div>

      {/* Tabel */}
      <div style={{ background:G.white, border:`1px solid ${G.border}`, borderRadius:10, overflow:'hidden' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:32, color:G.txt3 }}>{t('laden')}</div>
        ) : gefilterd.length === 0 ? (
          <div style={{ textAlign:'center', padding:32, color:G.txt3 }}>{t('geen_facturen_gevonden')}</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#1a364c', color:'white' }}>
                {[
                  {k:'factuurnr', l:t('factuurnr')}, {k:'factdatum', l:t('factuurdatum')},
                  {k:'vervaldatum', l:t('vervaldatum')}, {k:'route', l:t('route')},
                  {k:'zending', l:t('aanvraag')}, {k:'goederen', l:t('goederen')},
                  {k:'totaal', l:t('totaal')}, {k:'status', l:t('status')},
                  {k:'verloopt', l:t('verloopt_over')}, {k:'pdf', l:''},
                ].map(({k,l}) => (
                  <th key={k} style={{ padding:'11px 12px', textAlign: ['totaal','status','verloopt','pdf'].includes(k) ? 'right' : 'left', fontWeight:600, fontSize:12, letterSpacing:'.03em', whiteSpace:'nowrap' }}>{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gefilterd.map((f, i) => {
                const betaald = (f.Status||f.FactuurStatus) === 'Betaald';
                const dagen = !betaald ? verlooptOver(f.VervalDatum) : null;
                const verlopen = dagen !== null && dagen < 0;
                const bijna = dagen !== null && dagen >= 0 && dagen <= 7;
                return (
                  <tr key={f.FactuurNr||i} style={{ borderBottom:`1px solid ${G.borderL}`, background: i%2===0 ? G.white : G.bg }}>
                    <td style={{ padding:'11px 12px', fontWeight:700, color:G.navy }}>
                      <a href="#" onClick={e=>{e.preventDefault();openPdf(f.FactuurNr);}} style={{ color:G.navy, textDecoration:'none' }}>{f.FactuurNr||'—'}</a>
                    </td>
                    <td style={{ padding:'11px 12px', color:G.txt2 }}>{fmtD(f.FactuurDatum)}</td>
                    <td style={{ padding:'11px 12px', color: verlopen ? G.red : bijna ? G.orange : G.txt2, fontWeight: verlopen||bijna ? 600 : 400 }}>{fmtD(f.VervalDatum)}</td>
                    <td style={{ padding:'11px 12px', color:G.txt2, whiteSpace:'nowrap' }}>
                      {[f.LaadPlaats, f.LosPlaats].filter(Boolean).join(' → ') || '—'}
                    </td>
                    <td style={{ padding:'11px 12px', color:G.txt2 }}>{f.AanvraagID||'—'}</td>
                    <td style={{ padding:'11px 12px', color:G.txt2, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.Goederen||'—'}</td>
                    <td style={{ padding:'11px 12px', textAlign:'right', fontWeight:600, color:G.txt }}>{fmt(f.TotaalPrijs)}</td>
                    <td style={{ padding:'11px 12px', textAlign:'right' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20,
                        background: betaald ? '#d1fae5' : verlopen ? '#fee2e2' : '#fef3c7',
                        color: betaald ? '#065f46' : verlopen ? G.red : '#92400e' }}>
                        {betaald ? `✓ ${t('betaald')}` : verlopen ? `⚠ ${t('te_laat')}` : `○ ${t('openstaand')}`}
                      </span>
                    </td>
                    <td style={{ padding:'11px 12px', textAlign:'right', fontSize:12,
                      color: verlopen ? G.red : bijna ? G.orange : G.txt3,
                      fontWeight: verlopen||bijna ? 600 : 400 }}>
                      {betaald ? '—' : dagen === null ? '—' : verlopen ? `${Math.abs(dagen)} ${t('dgn')} ${t('te_laat')}` : `${dagen} ${t('dgn')}`}
                    </td>
                    <td style={{ padding:'11px 12px', textAlign:'right' }}>
                      {f.FactuurNr && (
                        <button onClick={() => openPdf(f.FactuurNr)}
                          style={{ background:G.navy, border:'none', color:'white', fontSize:11, fontWeight:600, cursor:'pointer', padding:'4px 10px', borderRadius:5 }}>
                          PDF ↓
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div style={{ padding:'10px 14px', borderTop:`1px solid ${G.borderL}`, fontSize:12, color:G.txt3 }}>
          {gefilterd.length} {t('facturen_weergegeven')}
        </div>
      </div>
    </div>
  );
};

// ─── HOOFD COMPONENT ──────────────────────────────────────────────────────────

// ─── Bordero sectie ───────────────────────────────────────────────────────────
const BorderoSection = ({ aanvragen, onOpenBordero, onHerhaal, klantInfo }) => {
  const { t } = useLanguage();
  const G = { navy:'#1a364c', gold:'#d4a848', white:'#ffffff', bg:'#f8fafc', border:'#e2e8f0', txt:'#1e293b', txt2:'#64748b', txt3:'#94a3b8', green:'#16a34a', orange:'#ea580c', red:'#dc2626' };
  
  const STATUS_KLEUR = {
    'Onderweg': G.gold, 'Opdracht verzonden': G.navy, 'Opdracht ontvangen': G.navy,
    'Afgeleverd': G.green, 'Betaald': G.green, 'Factuur verstuurd': G.green,
    'Prijs ontvangen': G.orange, 'Prijs geoffreerd': G.orange,
  };

  const gesorteerd = [...aanvragen].sort((a,b) => {
    const da = new Date(a.LaadDatum || a.Datum || 0);
    const db2 = new Date(b.LaadDatum || b.Datum || 0);
    return db2 - da;
  });

  return (
    <div>
      <div style={{ marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:600, color:G.txt, letterSpacing:'-.4px', margin:0 }}>{t('zendingdossiers')}</h2>
          <p style={{ fontSize:13, color:G.txt2, margin:'4px 0 0' }}>{t('zendingdossiers_sub')}</p>
        </div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {gesorteerd.map(a => {
          const kleur = STATUS_KLEUR[a.Status] || G.txt2;
          return (
            <div key={a.AanvraagID} style={{
              background:G.white, border:`1px solid ${G.border}`, borderRadius:10,
              padding:'14px 18px', display:'flex', alignItems:'center', gap:16,
              cursor:'pointer', transition:'box-shadow 0.15s',
            }}
              onClick={() => onOpenBordero(a)}
              onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow='none'}
            >
              <div style={{ width:44, height:44, borderRadius:8, background:G.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                📋
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontWeight:700, fontSize:14, color:G.navy }}>{a.AanvraagID}</span>
                  <span style={{ fontSize:11, fontWeight:600, color:kleur, background:kleur+'18', padding:'2px 8px', borderRadius:10 }}>{KLANT_STATUS(a.Status, t).label}</span>
                </div>
                <div style={{ fontSize:13, color:G.txt2 }}>
                  {a.LaadPlaats} → {a.LosPlaats}
                  {a.LaadDatum && <span style={{ marginLeft:8, color:G.txt3 }}>· {new Date(a.LaadDatum).toLocaleDateString('nl-NL')}</span>}
                </div>
              </div>
              <button
                onClick={e => { e.stopPropagation(); onHerhaal && onHerhaal(a); }}
                style={{ background:G.navy, color:'#fff', border:'none', borderRadius:7,
                  padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer',
                  flexShrink:0, whiteSpace:'nowrap' }}
                title={t('herhaal_zending')}
              >
                ↺ {t('herhaal')}
              </button>
              <div style={{ color:G.txt3, fontSize:18, flexShrink:0 }}>›</div>
            </div>
          );
        })}
      </div>


    </div>
  );
};

export default function CustomerDashboard() {
  const { t, taal, wisselTaal } = useLanguage();
  const navigate = useNavigate();
  const socketRef = useRef(null);

  // State
  const [activeSection, setActiveSection] = useState('home');
  const [klantInfo, setKlantInfo] = useState(null);
  const [aanvragen, setAanvragen] = useState([]);
  const [counts, setCounts] = useState({ actief:0, prijs:0, onderweg:0, afgerond:0, unreadMsg:0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Modals
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [aanvragenModal, setAanvragenModal] = useState(false);
  const [selectedAanvraag, setSelectedAanvraag] = useState(null);
  const [detailAanvraag, setDetailAanvraag] = useState(null);
  const [borderoAanvraag, setBorderoAanvraag] = useState(null);
  const [printSectie, setPrintSectie] = useState(null);
  const [detailPos, setDetailPos] = useState({ x: 80, y: 60 });
  const [detailTrackerNotes, setDetailTrackerNotes] = useState('');
  const [showFactuurPdf, setShowFactuurPdf] = useState('');
  const [aanvragenFilter, setAanvragenFilter] = useState(null);
  const [aanvragenStatusFilter, setAanvragenStatusFilter] = useState('alle');
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [herhaalAanvraag, setHerhaalAanvraag] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // ─ Auth check ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/customer/check-auth', { credentials:'include' })
      .then(r => r.json())
      .then(d => { if (!d.authenticated) navigate('/customer/login'); })
      .catch(() => navigate('/customer/login'));
  }, [navigate]);

  // ─ Data laden ─────────────────────────────────────────────────────────────
  const laadData = useCallback(async (toonLoader = false) => {
    if (toonLoader) setRefreshing(true);
    try {
      const [klantRes, aanvRes, msgRes] = await Promise.all([
        fetch('/api/customer/account', { credentials:'include' }),
        fetch('/api/customer/aanvragen', { credentials:'include' }),
        fetch('/api/customer/messages', { credentials:'include' }),
      ]);
      const klantData = await klantRes.json();
      const aanvData  = await aanvRes.json();
      const msgData   = await msgRes.json();

      if (klantData.success) setKlantInfo(klantData.customer || klantData.account);

      if (aanvData.success) {
        const list = aanvData.aanvragen || [];
        setAanvragen(list);

        const unreadMsg = (msgData.messages || []).filter(m => m.sender_type === 'admin' && !m.is_read).length;
        const actief = list.filter(a => {
          const sl = (a.Status||'').toLowerCase();
          return !sl.includes('afgerond') && !sl.includes('geannuleerd') && !sl.includes('afgeschreven');
        }).length;
        const prijs = list.filter(a => (a.Status||'').toLowerCase().includes('geoffreerd')).length;
        const onderweg = list.filter(a => (a.Status||'').toLowerCase().includes('onderweg')).length;
        const afgerond = list.filter(a => {
          const sl = (a.Status||'').toLowerCase();
          return sl.includes('afgerond') || sl.includes('afgeleverd');
        }).length;
        setCounts({ actief, prijs, onderweg, afgerond, unreadMsg });
      }
    } catch(e) {
      console.error('Laad fout:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    laadData();
    const interval = setInterval(laadData, 60000);
    return () => clearInterval(interval);
  }, [laadData]);

  // ─ Socket.IO ───────────────────────────────────────────────────────────────
  useEffect(() => {
    socketRef.current = io({ path:'/socket.io', transports:['websocket','polling'] });
    const sock = socketRef.current;
    sock.on('prijs-geoffreerd', laadData);
    sock.on('opdracht-ontvangen', laadData);
    sock.on('aanvraag-updated', laadData);
    sock.on('nieuw-admin-bericht', laadData);
    sock.on('geofence-event', (data) => {
      window.dispatchEvent(new CustomEvent('portal-geofence', { detail:data }));
    });
    return () => sock.disconnect();
  }, [laadData]);

  // ─ Uitloggen ──────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    try {
      await fetch('/api/customer/logout', { method:'POST', credentials:'include' });
    } catch(e) {}
    navigate('/customer/login');
  };

  // ─ Navigatie helpers ──────────────────────────────────────────────────────
  const nav = (section) => setActiveSection(section);

  const navItems = [
    { id:'home',      label:t('nav_dashboard'),  icon:'home',     badge:0 },
    { id:'aanvragen', label:t('nav_aanvragen'),  icon:'list',     badge:counts.actief },
    { id:'tracking',  label:t('nav_tracking'),   icon:'track',    badge: counts.onderweg > 0 ? counts.onderweg : 0, badgeColor:'#d4a848' },
    { id:'analytics', label:t('nav_analytics'),  icon:'analytics',badge:0 },
    { id:'facturen',  label:t('nav_facturen'),   icon:'factuur',  badge:0 },
    { id:'bordero',   label:t('nav_bordero'),    icon:'bordero',  badge:0 },
    { id:'vaste_zendingen', label:t('nav_vaste_zendingen') || 'Vaste zendingen', icon:'repeat', badge:0 },
    { id:'berichten', label:t('nav_berichten'),  icon:'msg',      badge:counts.unreadMsg },
    { id:'help',      label:t('nav_help'),       icon:'help',     badge:0 },
  ];

  const bedrijfsnaam = klantInfo?.bedrijfsnaam || klantInfo?.naam || klantInfo?.username || 'Klant';
  const kwalificatie = klantInfo?.kwalificatie || t('portaalgebruiker');
  // Contactpersoon voor begroeting (voornaam), anders eerste woord bedrijfsnaam
  const contactpersoon = klantInfo?.contactpersoon || '';
  const aanspreking = contactpersoon
    ? contactpersoon.split(' ')[0]
    : bedrijfsnaam.split(' ')[0];

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', minHeight:'100vh', background:G.cream, fontFamily:"-apple-system,'Segoe UI',sans-serif" }}>

      {/* ══ MOBILE OVERLAY ══ */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:998 }} />
      )}

      {/* ══ SIDEBAR ══ */}
      <aside style={{
        width:220, flexShrink:0, display:'flex', flexDirection:'column',
        height:'100vh', overflow:'hidden',
        background:G.navyDeep,
        backgroundImage:'repeating-linear-gradient(135deg,rgba(255,255,255,.012) 0px,rgba(255,255,255,.012) 1px,transparent 1px,transparent 40px)',
        borderRight:`1px solid ${G.border}`,
        // Sidebar altijd fixed
        position: 'fixed',
        top: 0, left: 0, bottom: 0, zIndex: 999,
        transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform .25s ease',
      }}>
        {/* Logo */}
        <div style={{ padding:'24px 20px 18px', borderBottom:`1px solid ${G.border}` }}>
          <div style={{ fontSize:20, fontWeight:700, color:G.white, letterSpacing:'-.5px' }}>SendWell</div>
          <div style={{ fontSize:11, color:G.gold, letterSpacing:'.12em', textTransform:'uppercase', marginTop:2 }}>Klantenportaal</div>
        </div>

        {/* Gebruiker */}
        <div style={{ padding:'14px 20px 16px', borderBottom:`1px solid ${G.border}` }}>
          <div style={{ fontSize:13, fontWeight:500, color:'rgba(255,255,255,.85)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{bedrijfsnaam}</div>
          <div style={{ display:'inline-block', fontSize:10, color:G.gold, background:G.goldDim, padding:'2px 8px', borderRadius:20, marginTop:4, letterSpacing:'.05em' }}>{kwalificatie}</div>
        </div>

        {/* Klok — altijd zichtbaar */}
        <SidebarClock />

        {/* Navigatie */}
        <nav style={{ padding:'12px 10px', flex:1, overflowY:'auto', minHeight:0 }}>
          <div style={{ fontSize:10, color:'rgba(255,255,255,.28)', letterSpacing:'.12em', textTransform:'uppercase', padding:'0 10px 6px', marginTop:4 }}>{t('overzicht')}</div>
          {navItems.slice(0, 2).map(item => (
            <NavItem key={item.id} item={item} active={activeSection === item.id} onClick={() => nav(item.id)} />
          ))}
          <div style={{ fontSize:10, color:'rgba(255,255,255,.28)', letterSpacing:'.12em', textTransform:'uppercase', padding:'0 10px 6px', marginTop:14 }}>{t('logistiek')}</div>
          {navItems.slice(2, 4).map(item => (
            <NavItem key={item.id} item={item} active={activeSection === item.id} onClick={() => nav(item.id)} />
          ))}
          <div style={{ fontSize:10, color:'rgba(255,255,255,.28)', letterSpacing:'.12em', textTransform:'uppercase', padding:'0 10px 6px', marginTop:14 }}>{t('administratie')}</div>
          {navItems.slice(4).map(item => (
            <NavItem key={item.id} item={item} active={activeSection === item.id} onClick={() => nav(item.id)} />
          ))}
        </nav>

        {/* Bodem */}
        <div style={{ padding:'14px 10px', borderTop:`1px solid ${G.border}` }}>
          <TaalWisselaar stijl='sidebar' />
          <NavItem item={{ id:'settings', label:t('nav_instellingen'), icon:'settings', badge:0 }} active={false} onClick={() => setShowSettings(true)} />
          <NavItem item={{ id:'afdrukken', label:t('nav_afdrukken'), icon:'print', badge:0 }} active={false} onClick={() => setPrintSectie(activeSection)} />
          <NavItem item={{ id:'logout', label:t('nav_uitloggen'), icon:'logout', badge:0 }} active={false} onClick={handleLogout} />
        </div>
      </aside>

      {/* ══ CONTENT ══ */}
      <main style={{ flex:1, overflow: activeSection === 'tracking' ? 'hidden' : 'auto', display:'flex', flexDirection:'column', minWidth:0, marginLeft: isMobile ? 0 : 220 }}>
        {/* ── Mobiele topbalk ── */}
        <div style={{
          display: 'none', // via inline style overschreven op mobiel
          alignItems:'center', justifyContent:'space-between',
          padding:'12px 16px', background:G.navyDeep,
          position:'sticky', top:0, zIndex:100,
          ...(isMobile ? { display:'flex' } : {})
        }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ background:'none', border:'none', color:'white', cursor:'pointer', padding:4, display:'flex', flexDirection:'column', gap:5 }}>
            <span style={{ display:'block', width:22, height:2, background:'white', borderRadius:2 }} />
            <span style={{ display:'block', width:22, height:2, background:'white', borderRadius:2 }} />
            <span style={{ display:'block', width:22, height:2, background:'white', borderRadius:2 }} />
          </button>
          <div style={{ fontSize:16, fontWeight:700, color:'white', letterSpacing:'-.3px' }}>SendWell</div>
          <div style={{ width:30 }} />
        </div>

        {loading ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', minHeight:400 }}>
            <div style={{ textAlign:'center', color:G.txt2 }}>
              <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>
              <div style={{ fontSize:14 }}>Laden...</div>
            </div>
          </div>
        ) : (
          <div style={{ padding: activeSection === 'tracking' ? '0' : (isMobile ? '16px' : '28px 32px'), flex: activeSection === 'tracking' ? 1 : 'none', display: activeSection === 'tracking' ? 'flex' : 'block', flexDirection:'column' }}>

            {/* ── HOME ── */}
            {activeSection === 'home' && (
              <HomeSection
                counts={counts}
                aanvragen={aanvragen}
                bedrijfsnaam={bedrijfsnaam}
                aanspreking={aanspreking}
                refreshing={refreshing}
                onRefresh={() => laadData(true)}
                onNavigate={nav}
                onOpenAanvraag={(a) => {
                  setDetailAanvraag(a);
                  setDetailTrackerNotes('');
                  setDetailPos({ x: Math.max(40, (window.innerWidth - 680) / 2), y: 60 });
                  if (a.TrackerID) {
                    fetch('/api/customer/tracker-notes/' + a.TrackerID, { credentials:'include' })
                      .then(r => r.json()).then(d => { if (d.notes) setDetailTrackerNotes(d.notes); }).catch(() => {});
                  }
                }}
                onNewRequest={() => setShowNewRequest(true)}
                onOpenAkkoord={() => { setAanvragenFilter('Prijs ontvangen'); setAanvragenModal(true); }}
              />
            )}

            {/* ── AANVRAGEN ── */}
            {activeSection === 'aanvragen' && (
              <div>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
                  <div>
                    <h2 style={{ fontSize:22, fontWeight:600, color:G.txt, letterSpacing:'-.4px', margin:0 }}>{t('nav_aanvragen')}</h2>
                    <p style={{ fontSize:13, color:G.txt2, margin:'4px 0 0' }}>{t('aanvragen_overzicht')}</p>
                  </div>
                  <button onClick={() => setShowNewRequest(true)} style={{ display:'flex', alignItems:'center', gap:6, background:G.navy, color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:500, cursor:'pointer' }}>
                    <Icon name="plus" size={14} /> {t('nieuwe_aanvraag')}
                  </button>
                </div>
                <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
                  {[
                    { v:'alle',                label: t('alle_statussen') },
                    { v:'Aanvraag ingediend',  label: t('status_aanvraag_ingediend') },
                    { v:'Prijs ontvangen',     label: t('status_prijs_ontvangen') },
                    { v:'Akkoord gegeven',     label: t('status_akkoord_gegeven') },
                    { v:'Opdracht ontvangen',  label: t('status_opdracht_ontvangen') },
                    { v:'Opdracht verzonden',  label: t('status_opdracht_verzonden') },
                    { v:'Onderweg',            label: t('status_onderweg') },
                    { v:'Afgeleverd',          label: t('status_afgeleverd') },
                    { v:'Afgerond',            label: t('status_afgerond') },
                  ].map(({ v, label }) => (
                    <button key={v} onClick={() => setAanvragenStatusFilter(v)}
                      style={{ padding:'5px 14px', borderRadius:20, border:`1px solid ${aanvragenStatusFilter===v ? G.navy : G.borderL}`,
                        background: aanvragenStatusFilter===v ? G.navy : G.white,
                        color: aanvragenStatusFilter===v ? '#fff' : G.txt2,
                        fontSize:12, fontWeight:500, cursor:'pointer', whiteSpace:'nowrap' }}>
                      {v === 'alle' ? `${label} (${aanvragen.length})` : `${label} (${aanvragen.filter(a=>(a.Status||'').toLowerCase().includes(v.toLowerCase())).length})`}
                    </button>
                  ))}
                </div>
                <div style={{ background:G.white, border:`1px solid ${G.borderL}`, borderRadius:10, padding:'0 4px', overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                  <AanvragenTabel
                    aanvragen={aanvragenStatusFilter === 'alle' ? aanvragen : aanvragen.filter(a => (a.Status||'').toLowerCase().includes(aanvragenStatusFilter.toLowerCase()))}
                    onOpen={(a) => {
                      setDetailAanvraag(a);
                      setDetailTrackerNotes('');
                      setDetailPos({ x: Math.max(40, (window.innerWidth - 680) / 2), y: 60 });
                      if (a.TrackerID) {
                        fetch('/api/customer/tracker-notes/' + a.TrackerID, { credentials:'include' })
                          .then(r => r.json()).then(d => { if (d.notes) setDetailTrackerNotes(d.notes); }).catch(() => {});
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* ── TRACK & TRACE ── */}
            {activeSection === 'tracking' && (
              <TrackingSection aanvragen={aanvragen} klantInfo={klantInfo} />
            )}

            {/* ── ANALYTICS ── */}
            {activeSection === 'analytics' && (
              <CustomerAnalytics aanvragen={aanvragen} />
            )}

            {/* ── FACTUREN ── */}
            {activeSection === 'facturen' && (
              <FacturenSection klantInfo={klantInfo} />
            )}

            {/* ── BORDERO ── */}
            {activeSection === 'bordero' && (
              <BorderoSection aanvragen={aanvragen} onOpenBordero={(a) => setBorderoAanvraag(a)} klantInfo={klantInfo} onHerhaal={a => { setHerhaalAanvraag(a); setShowNewRequest(true); }} />
            )}

            {/* ── BERICHTEN ── */}
            {activeSection === 'vaste_zendingen' && (
              <div style={{ padding:'24px 0' }}>
                <VasteZendingenBeheer klantId={klantInfo?.KlantID} isKlantPortaal={true} />
              </div>
            )}
            {activeSection === 'berichten' && (
              <div>
                <div style={{ marginBottom:20 }}>
                  <h2 style={{ fontSize:22, fontWeight:600, color:G.txt, letterSpacing:'-.4px', margin:0 }}>{t('nav_berichten')}</h2>
                  <p style={{ fontSize:13, color:G.txt2, margin:'4px 0 0' }}>{t('communicatie_sub')}</p>
                </div>
                <div style={{ background:G.white, border:`1px solid ${G.borderL}`, borderRadius:10, overflow:'hidden' }}>
                  <CustomerMessages embedded={true} />
                </div>
              </div>
            )}

            {/* ── HELP ── */}
            {activeSection === 'help' && (
              <div>
                <div style={{ marginBottom:20 }}>
                  <h2 style={{ fontSize:22, fontWeight:600, color:G.txt, letterSpacing:'-.4px', margin:0 }}>{t('nav_help')}</h2>
                  <p style={{ fontSize:13, color:G.txt2, margin:'4px 0 0' }}>{t('help_sub')}</p>
                </div>
                <CustomerHelpCenter embedded={true} />
              </div>
            )}

          </div>
        )}
      </main>

      {/* ══ MODALS ══ */}
      {/* Detail panel voor aanvragen sectie */}
      {detailAanvraag && (
        <AanvraagDetailPanel
          detailAanvraag={detailAanvraag}
          detailPos={detailPos}
          setDetailPos={setDetailPos}
          detailTrackerNotes={detailTrackerNotes}
          setDetailAanvraag={setDetailAanvraag}
          setShowFactuurPdf={setShowFactuurPdf}
          onOpenBordero={(a) => { setBorderoAanvraag(a); }}
        />
      )}
      {printSectie && (
        <PrintDialog
          sectie={printSectie}
          aanvragen={aanvragen}
          klantInfo={klantInfo}
          onClose={() => setPrintSectie(null)}
        />
      )}
      {borderoAanvraag && (
        <CustomerBordero
          aanvraag={borderoAanvraag}
          onClose={() => setBorderoAanvraag(null)}
        />
      )}
      {showFactuurPdf && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10001 }} onClick={() => setShowFactuurPdf('')}>
          <div className="bg-white shadow-2xl w-full max-w-4xl border-2 border-blue-900" style={{ height: '85vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="bg-blue-900 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold">Factuur {showFactuurPdf}</h3>
              <button onClick={() => setShowFactuurPdf('')} className="w-8 h-8 flex items-center justify-center hover:bg-blue-800 text-xl">&times;</button>
            </div>
            <iframe src={'/api/customer/factuur-pdf/' + showFactuurPdf} className="w-full border-0" style={{ height: 'calc(85vh - 56px)' }} title="Factuur" />
          </div>
        </div>
      )}

      {aanvragenModal && (
        <CustomerAanvragenModal
          show={aanvragenModal}
          onClose={() => { setAanvragenModal(false); setSelectedAanvraag(null); setAanvragenFilter(null); }}
          initialStatusFilter={aanvragenFilter}
          onDataChange={laadData}
          title={t('nav_aanvragen')}
        />
      )}

      {showNewRequest && (
        <DraggablePopup
          title={t('nieuwe_aanvraag')}
          onClose={() => setShowNewRequest(false)}
          width="min(95vw, 1200px)"
        >
          <CustomerNewRequest onClose={() => { setShowNewRequest(false); setHerhaalAanvraag(null); }} onSuccess={() => { setShowNewRequest(false); setHerhaalAanvraag(null); laadData(); }} initialAanvraag={herhaalAanvraag} />
        </DraggablePopup>
      )}

      {showSettings && (
        <CustomerSettingsModal
          show={showSettings}
          onClose={() => setShowSettings(false)}
          customerId={klantInfo?.id}
        />
      )}

      <GeofencePortalToast />
    </div>
  );
}


// ─── SidebarClock — altijd zichtbaar ────────────────────────────────────────
function SidebarClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const tijd = now.toLocaleTimeString('nl-NL', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
  const datum = now.toLocaleDateString('nl-NL', { weekday:'short', day:'numeric', month:'short' });
  return (
    <div style={{
      padding:'12px 20px',
      borderBottom:`1px solid ${G.border}`,
      textAlign:'center',
    }}>
      <div style={{ fontSize:22, fontWeight:700, color:G.white, letterSpacing:'2px', fontVariantNumeric:'tabular-nums' }}>
        {tijd}
      </div>
      <div style={{ fontSize:11, color:G.gold, letterSpacing:'.08em', marginTop:2, textTransform:'capitalize' }}>
        {datum}
      </div>
    </div>
  );
}

// ─── NavItem component ────────────────────────────────────────────────────────
function NavItem({ item, active, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display:'flex', alignItems:'center', gap:11, padding:'10px 12px',
        borderRadius:6, marginBottom:2, cursor:'pointer',
        color: active ? '#fff' : hover ? 'rgba(255,255,255,.85)' : 'rgba(255,255,255,.55)',
        fontSize:13.5, fontWeight: active ? 500 : 400,
        background: active ? G.navyLight : hover ? 'rgba(255,255,255,.05)' : 'transparent',
        border: active ? '1px solid rgba(255,255,255,.1)' : '1px solid transparent',
        position:'relative', transition:'all .15s',
      }}
    >
      {active && (
        <div style={{ position:'absolute', left:0, top:'20%', bottom:'20%', width:3, background:G.gold, borderRadius:'0 2px 2px 0' }} />
      )}
      <div style={{ opacity: active ? 1 : hover ? .85 : .7 }}>
        <Icon name={item.icon} size={16} />
      </div>
      {item.label}
      {item.badge > 0 && (
        <span style={{ marginLeft:'auto', background: item.badgeColor || G.red, color:'white', fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:20, lineHeight:1.4 }}>
          {item.badge}
        </span>
      )}
    </div>
  );
}


// ─── DraggablePopup component ─────────────────────────────────────────────────
function DraggablePopup({ title, onClose, children, width = '900px' }) {
  const [pos, setPos] = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });
  const ref = useRef(null);

  // Centreer bij eerste render
  useEffect(() => {
    if (ref.current) {
      const w = ref.current.offsetWidth;
      const h = ref.current.offsetHeight;
      setPos({
        x: Math.max(0, (window.innerWidth - w) / 2),
        y: Math.max(0, (window.innerHeight - h) / 2),
      });
    }
  }, []);

  const onMouseDown = (e) => {
    if (!e.target.closest('.sw-drag-header')) return;
    setDragging(true);
    offset.current = { x: e.clientX - (pos.x || 0), y: e.clientY - (pos.y || 0) };
    e.preventDefault();
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => setPos({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y });
    const up = () => setDragging(false);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [dragging]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:9999 }}>
      <div
        ref={ref}
        onMouseDown={onMouseDown}
        style={{
          position:'fixed',
          left: pos.x ?? '50%',
          top: pos.y ?? '50%',
          transform: pos.x === null ? 'translate(-50%,-50%)' : 'none',
          width, maxHeight:'90vh',
          background:G.white,
          borderRadius:12,
          boxShadow:'0 16px 56px rgba(26,54,76,.25), 0 2px 8px rgba(0,0,0,.1)',
          display:'flex',
          flexDirection:'column',
          overflow:'hidden',
        }}
      >
        {/* Header — sleepbaar */}
        <div className="sw-drag-header" style={{
          background:`linear-gradient(135deg, ${G.navy} 0%, ${G.navyMid} 100%)`,
          padding:'14px 20px', display:'flex', justifyContent:'space-between',
          alignItems:'center', cursor:'move', userSelect:'none', flexShrink:0,
        }}>
          <span style={{ fontSize:16, fontWeight:700, color:G.white }}>{title}</span>
          <button onClick={onClose} style={{
            background:'rgba(255,255,255,.15)', border:'none', color:G.white,
            width:28, height:28, borderRadius:'50%', cursor:'pointer',
            fontSize:14, display:'flex', alignItems:'center', justifyContent:'center',
          }}>✕</button>
        </div>
        {/* Gouden lijn */}
        <div style={{ height:3, background:G.gold, flexShrink:0 }} />
        {/* Inhoud */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── HomeSection ──────────────────────────────────────────────────────────────
function HomeSection({ counts, aanvragen, bedrijfsnaam, aanspreking, refreshing, onRefresh, onNavigate, onOpenAanvraag, onNewRequest, onOpenAkkoord }) {
  const { t, taal } = useLanguage();
  const locale = { nl:'nl-NL', en:'en-GB', de:'de-DE', fr:'fr-FR' };
  const nu = new Date().toLocaleDateString(locale[taal] || 'nl-NL', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  const uur = new Date().getHours();
  const dagdeel = uur < 12 ? t('dashboard_welkom') : uur < 18 ? (t('dashboard_welkom_middag') || t('dashboard_welkom')) : (t('dashboard_welkom_avond') || t('dashboard_welkom'));
  const recent = [...aanvragen]
    .sort((a, b) => new Date(b.Datum || 0) - new Date(a.Datum || 0))
    .slice(0, 4);
  const lopend = aanvragen.filter(a => {
    const sl = (a.Status||'').toLowerCase();
    return !sl.includes('afgerond') && !sl.includes('geannuleerd') && !sl.includes('afgeschreven');
  }).slice(0, 5);

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign:'center', marginBottom:24 }}>
        <h2 style={{ fontSize:22, fontWeight:600, color:G.txt, letterSpacing:'-.4px', margin:'0 0 4px' }}>
          {dagdeel}, {aanspreking}
        </h2>
        <p style={{ fontSize:13, color:G.txt2, margin:'0 0 16px' }}>
          {t('dashboard_hier_overzicht') || 'Hier is uw overzicht voor'} {nu}
        </p>
        <button onClick={onNewRequest} style={{ display:'inline-flex', alignItems:'center', gap:6, background:G.navy, color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontSize:14, fontWeight:500, cursor:'pointer' }}>
          <Icon name="plus" size={14} /> {t('nieuwe_aanvraag')}
        </button>
      </div>

      {/* KPI cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(0,1fr))', gap:14, marginBottom:20 }}>
        <KpiCard label={t('dashboard_actieve_zendingen')} value={counts.actief} trend={`${counts.onderweg} ${t('dashboard_onderweg')}`} trendUp={null} accent={`linear-gradient(90deg,#3b82f6,#60a5fa)`} />
        <div onClick={() => counts.prijs > 0 && onOpenAkkoord()} style={{ cursor: counts.prijs > 0 ? 'pointer' : 'default' }}>
          <KpiCard
            label={t("wacht_op_bevestiging")}
            value={counts.prijs}
            trend={counts.prijs > 0 ? `👆 ${t('klik_factuur') || 'Klik om akkoord te geven'}` : t('dashboard_alles_in_orde')}
            trendUp={counts.prijs === 0}
            accent={counts.prijs > 0 ? `linear-gradient(90deg,${G.red},#f87171)` : `linear-gradient(90deg,${G.green},#34d399)`}
            highlight={counts.prijs > 0}
          />
        </div>
        <KpiCard label={t('dashboard_afgerond_dit_jaar') || t('status_afgerond')} value={counts.afgerond} trend={t('succesvol_afgeleverd') || 'Succesvol afgeleverd'} trendUp={true} accent={`linear-gradient(90deg,${G.green},#34d399)`} />
        <KpiCard label={t('dashboard_ongelezen_berichten')} value={counts.unreadMsg} trend={counts.unreadMsg > 0 ? t('klik_om_te_lezen') : t('alles_gelezen')} trendUp={counts.unreadMsg === 0} accent={`linear-gradient(90deg,${G.red},#f87171)`} />
      </div>

      {/* Snelkoppelingen */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
        {[
          { icon:'📦', label:t('nieuwe_aanvraag'), sub:t('transport_inplannen'), onClick: onNewRequest },
          { icon:'🛰', label:t('nav_tracking'),   sub:`${counts.onderweg} ${t('zendingen').toLowerCase()} ${t('live').toLowerCase()}`, onClick: () => onNavigate('tracking') },
          { icon:'📊', label:t('nav_analytics'),       sub:t('dashboard_kosten_routes'),   onClick: () => onNavigate('analytics') },
        ].map(({ icon, label, sub, onClick }) => (
          <div key={label} onClick={onClick} style={{ background:G.white, border:`1px solid ${G.borderL}`, borderRadius:10, padding:16, display:'flex', alignItems:'center', gap:12, cursor:'pointer', transition:'.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor='#cbd5e1'; e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=G.borderL; e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}
          >
            <div style={{ width:36, height:36, borderRadius:8, background:'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{icon}</div>
            <div>
              <div style={{ fontSize:13, fontWeight:500, color:G.txt }}>{label}</div>
              <div style={{ fontSize:11.5, color:G.txt2, marginTop:1 }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Twee kolommen */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        {/* Recente activiteit */}
        <div style={{ background:G.white, border:`1px solid ${G.borderL}`, borderRadius:10, padding:'20px 22px' }}>
          <div style={{ fontSize:13, fontWeight:600, color:G.txt2, letterSpacing:'.04em', textTransform:'uppercase', marginBottom:14 }}>{t('dashboard_recente_activiteit')}</div>
          {recent.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px', color:G.txt3, fontSize:13 }}>Geen aanvragen gevonden</div>
          ) : (
            recent.map((a, i) => {
              const st = KLANT_STATUS(a.Status, t);
              const los = [a.LosPlaats, a.LosLand].filter(Boolean).join(', ') || '—';
              return (
                <EventRow
                  key={a.AanvraagID || i}
                  icon={st.label.includes('weg') ? '🚛' : st.label.includes('Betaal') ? '💶' : st.label.includes('Prijs') ? '💬' : '📦'}
                  iconBg={st.bg}
                  title={`${a.AanvraagID} — ${los}`}
                  sub={st.label}
                  time={a.Datum ? String(a.Datum).slice(5, 10).replace('-', ' ') : ''}
                />
              );
            })
          )}
        </div>

        {/* Lopende aanvragen */}
        <div style={{ background:G.white, border:`1px solid ${G.borderL}`, borderRadius:10, padding:'20px 22px', overflow:'hidden' }}>
          <div style={{ fontSize:13, fontWeight:600, color:G.txt2, letterSpacing:'.04em', textTransform:'uppercase', marginBottom:14 }}>{t('lopende_aanvragen')}</div>
          <AanvragenTabel aanvragen={lopend} onOpen={onOpenAanvraag} compact />
          {aanvragen.length > 5 && (
            <button onClick={() => onNavigate('aanvragen')} style={{ marginTop:12, background:'none', border:'none', color:G.blue, fontSize:13, cursor:'pointer', fontWeight:500, padding:0 }}>
              {t('alle_statussen').split(' ')[0]} {aanvragen.length} {t('nav_aanvragen').toLowerCase()} →
            </button>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}
